import {
  TrendingUp,
  Users,
  Crown,
  Building2,
  Heart,
  Star,
  Calendar,
  Clock,
  CreditCard,
  Mail,
  BarChart3,
  Globe,
  Headphones,
  Shield,
  Zap,
  Palette,
} from 'lucide-react';
import { PricingTier, FeatureCategory, FAQItem } from './types';

export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'growth',
    name: 'Growth',
    description: 'For businesses focused on customer engagement and visibility',
    monthlyPrice: 29,
    yearlyPrice: 290,
    accent: 'grow',
    icon: TrendingUp,
    features: [
      'Customer Reviews Collection',
      'Loyalty Programs (Apple & Google Wallet)',
      'Email Marketing Campaigns',
      'Review Analytics & Insights',
      'Coupon & Discount Management',
      'Custom Review Forms',
      'Email Support',
    ],
    ctaText: 'Start Growing',
    ctaLink: '/sign-up?plan=growth',
  },
  {
    id: 'management',
    name: 'Management',
    description: 'For businesses focused on workforce operations and efficiency',
    monthlyPrice: 39,
    yearlyPrice: 390,
    accent: 'primary',
    icon: Users,
    features: [
      'Employee Management',
      'Shift Scheduling & Calendar',
      'QR Code Time Tracking',
      'Payroll for 25+ EU Countries',
      'Employee Portal Access',
      'Contracts & Documents',
      'Email Support',
    ],
    ctaText: 'Start Managing',
    ctaLink: '/sign-up?plan=management',
  },
  {
    id: 'complete',
    name: 'Complete',
    description: 'The full solution for growing and managing your business',
    monthlyPrice: 59,
    yearlyPrice: 590,
    accent: 'secondary',
    icon: Crown,
    isPopular: true,
    features: [
      'Everything in Growth',
      'Everything in Management',
      'Advanced Analytics & Reporting',
      'Priority Support',
      'Custom Branding Options',
      'Multi-Location Support',
      'API Access',
    ],
    ctaText: 'Get Complete',
    ctaLink: '/sign-up?plan=complete',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Custom solutions for large organizations with specific needs',
    monthlyPrice: null,
    yearlyPrice: null,
    accent: 'gradient',
    icon: Building2,
    features: [
      'Everything in Complete',
      'Custom Website Development',
      'Reservation System',
      'In-Restaurant Ordering',
      'Dedicated Account Manager',
      'Custom Integrations',
      'SLA Guarantee',
      'On-Premise Option',
    ],
    ctaText: 'Contact Sales',
    ctaLink: '/contact?plan=enterprise',
  },
];

export const FEATURE_CATEGORIES: FeatureCategory[] = [
  {
    id: 'customer-engagement',
    name: 'Customer Engagement',
    icon: Heart,
    features: [
      { name: 'Customer Reviews', growth: true, management: false, complete: true, enterprise: true },
      { name: 'Loyalty Programs', growth: true, management: false, complete: true, enterprise: true },
      { name: 'Digital Wallet Passes', growth: true, management: false, complete: true, enterprise: true },
      { name: 'Email Marketing', growth: true, management: false, complete: true, enterprise: true },
      { name: 'Coupon Management', growth: true, management: false, complete: true, enterprise: true },
      { name: 'Review Analytics', growth: true, management: false, complete: true, enterprise: true },
      { name: 'SMS Campaigns', growth: 'Add-on', management: false, complete: true, enterprise: 'Unlimited' },
    ],
  },
  {
    id: 'workforce-management',
    name: 'Workforce Management',
    icon: Users,
    features: [
      { name: 'Employee Profiles', growth: false, management: true, complete: true, enterprise: true },
      { name: 'Shift Scheduling', growth: false, management: true, complete: true, enterprise: true },
      { name: 'Time Tracking (QR)', growth: false, management: true, complete: true, enterprise: true },
      { name: 'Payroll (25+ EU)', growth: false, management: true, complete: true, enterprise: true },
      { name: 'Employee Portal', growth: false, management: true, complete: true, enterprise: true },
      { name: 'Contracts & Docs', growth: false, management: true, complete: true, enterprise: true },
      { name: 'Multi-Location Teams', growth: false, management: 'Limited', complete: true, enterprise: 'Unlimited' },
    ],
  },
  {
    id: 'analytics-reporting',
    name: 'Analytics & Reporting',
    icon: BarChart3,
    features: [
      { name: 'Basic Dashboard', growth: true, management: true, complete: true, enterprise: true },
      { name: 'Review Analytics', growth: true, management: false, complete: true, enterprise: true },
      { name: 'Employee Insights', growth: false, management: true, complete: true, enterprise: true },
      { name: 'Advanced Analytics', growth: false, management: false, complete: true, enterprise: true },
      { name: 'Custom Reports', growth: false, management: false, complete: true, enterprise: true },
      { name: 'Data Export', growth: 'CSV', management: 'CSV', complete: 'All formats', enterprise: 'All formats + API' },
    ],
  },
  {
    id: 'platform-support',
    name: 'Platform & Support',
    icon: Headphones,
    features: [
      { name: 'Email Support', growth: true, management: true, complete: true, enterprise: true },
      { name: 'Priority Support', growth: false, management: false, complete: true, enterprise: true },
      { name: 'Dedicated Manager', growth: false, management: false, complete: false, enterprise: true },
      { name: 'API Access', growth: false, management: false, complete: true, enterprise: true },
      { name: 'Custom Branding', growth: false, management: false, complete: true, enterprise: true },
      { name: 'SSO / SAML', growth: false, management: false, complete: false, enterprise: true },
      { name: 'SLA Guarantee', growth: false, management: false, complete: false, enterprise: true },
    ],
  },
  {
    id: 'custom-development',
    name: 'Custom Development',
    icon: Globe,
    features: [
      { name: 'Custom Website', growth: false, management: false, complete: false, enterprise: true },
      { name: 'Reservation System', growth: false, management: false, complete: false, enterprise: true },
      { name: 'In-Restaurant Ordering', growth: false, management: false, complete: false, enterprise: true },
      { name: 'Custom Integrations', growth: false, management: false, complete: false, enterprise: true },
      { name: 'White-Label Solution', growth: false, management: false, complete: false, enterprise: true },
    ],
  },
];

export const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'Can I switch plans at any time?',
    answer: 'Yes! You can upgrade or downgrade your plan at any time. When upgrading, you\'ll be charged the prorated difference immediately. When downgrading, the credit will be applied to your next billing cycle. All your data and settings are preserved.',
  },
  {
    question: 'Is there a free trial?',
    answer: 'Yes, all paid plans include a 14-day free trial with full access to all features. A credit card is required to start, but you won\'t be charged until the trial ends. You can cancel anytime during the trial.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, Mastercard, American Express), SEPA direct debit for European customers, and can arrange invoicing for Enterprise plans. All payments are securely processed through Stripe.',
  },
  {
    question: 'What happens when I exceed my plan limits?',
    answer: 'We\'ll notify you before you reach your limits so you have time to plan. You can upgrade anytime to get more capacity, or we can discuss custom solutions tailored to your specific needs.',
  },
  {
    question: 'Do you offer discounts for non-profits or startups?',
    answer: 'Yes! We offer special pricing for registered non-profit organizations and early-stage startups (under 2 years old). Contact our sales team with your organization details to learn more.',
  },
  {
    question: 'Can I cancel my subscription?',
    answer: 'Absolutely. You can cancel your subscription at any time from your account settings. You\'ll continue to have access until the end of your current billing period. We don\'t believe in lock-in contracts.',
  },
  {
    question: 'What\'s included in the Enterprise plan?',
    answer: 'Enterprise includes everything in Complete, plus custom development options like websites, reservation systems, and in-restaurant ordering. You also get a dedicated account manager, custom integrations, SLA guarantees, and the option for on-premise deployment.',
  },
  {
    question: 'How does the yearly billing save money?',
    answer: 'When you choose yearly billing, you save 17% compared to monthly billing. For example, Complete is €59/month or €590/year (equivalent to €49/month). You\'re billed once annually and can still cancel anytime.',
  },
];

// Helper to get tier by ID
export const getTierById = (id: string): PricingTier | undefined => {
  return PRICING_TIERS.find(tier => tier.id === id);
};

// Calculate yearly savings percentage
export const YEARLY_SAVINGS_PERCENT = 17;
